import {
  defineSpaceParameterPanelValues,
  type DefinedSpaceParameterPanelValueSupport,
  type SpaceParameterPanelValueDefinitions,
} from '@src/components/AppLayout/SpaceParameters';
import type { AllInTier, FeeTier, SymbolGroup } from '@talos/kyoko';
import { DealerSpaceParameterKey } from './types';

export interface DealerSpaceInfluenceRecordByCustomerConfigurationField {
  AllInTier: AllInTier;
  FeeTier: FeeTier;
  SymbolGroup: SymbolGroup;
}

export type DealerSpaceParameterizationInfluenceField = keyof DealerSpaceInfluenceRecordByCustomerConfigurationField;

export interface DealerEntityAdminSpaceInfluence {
  /**
   * Promotes Counterparty to the controller so its selected customer can determine which EntityAdmin row to highlight.
   */
  parameterKey: typeof DealerSpaceParameterKey.Counterparty;
  customerConfigurationField: DealerSpaceParameterizationInfluenceField;
}

export type DealerEntityAdminSpaceSupport<
  TDefinitions extends SpaceParameterPanelValueDefinitions,
  TInfluence extends DealerEntityAdminSpaceInfluence = DealerEntityAdminSpaceInfluence,
> = DefinedSpaceParameterPanelValueSupport<TDefinitions> & {
  influence: TInfluence;
  influenceParameterKeys: readonly [typeof DealerSpaceParameterKey.Counterparty];
};

export function defineDealerEntityAdminSpaceSupport<
  const TDefinitions extends SpaceParameterPanelValueDefinitions,
  const TInfluence extends DealerEntityAdminSpaceInfluence,
>({
  filters,
  influence,
}: {
  filters: TDefinitions;
  influence: TInfluence;
}): DealerEntityAdminSpaceSupport<TDefinitions, TInfluence> {
  return {
    ...defineSpaceParameterPanelValues(filters),
    influence,
    influenceParameterKeys: [influence.parameterKey],
  };
}

export const CUSTOMER_ACCOUNT_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
  [DealerSpaceParameterKey.MarketAccounts]: 'MarketAccounts',
  [DealerSpaceParameterKey.Symbols]: 'Symbols',
});
export const CUSTOMER_ACCOUNT_CONFIGURATIONS_SPACE_PARAMETER_KEYS =
  CUSTOMER_ACCOUNT_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT.keys;

export const CUSTOMER_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparty',
  [DealerSpaceParameterKey.Modes]: 'Modes',
  [DealerSpaceParameterKey.AllInTiers]: 'AllInTiers',
  [DealerSpaceParameterKey.FeeTiers]: 'FeeTiers',
  [DealerSpaceParameterKey.SymbolGroups]: 'SymbolGroups',
});
export const CUSTOMER_CONFIGURATIONS_SPACE_PARAMETER_KEYS = CUSTOMER_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT.keys;

export const CUSTOMER_CREDITS_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
});

export const CUSTOMER_ADDRESSES_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparty',
  [DealerSpaceParameterKey.Currencies]: 'Currencies',
});

export const CUSTOMER_SECURITY_MASTER_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Symbols]: 'Symbols',
  [DealerSpaceParameterKey.Currencies]: 'Currencies',
  [DealerSpaceParameterKey.BasePairs]: 'BasePairs',
});
export const CUSTOMER_SECURITY_MASTER_SPACE_PARAMETER_KEYS = CUSTOMER_SECURITY_MASTER_SPACE_PARAMETER_SUPPORT.keys;

export const CUSTOMER_SYMBOL_GROUPS_SPACE_PARAMETER_SUPPORT = defineDealerEntityAdminSpaceSupport({
  filters: {
    [DealerSpaceParameterKey.Modes]: 'Modes',
    [DealerSpaceParameterKey.SymbolGroups]: 'SymbolGroups',
  },
  influence: {
    parameterKey: DealerSpaceParameterKey.Counterparty,
    customerConfigurationField: 'SymbolGroup',
  },
});
export const CUSTOMER_SYMBOL_GROUPS_SPACE_PARAMETER_KEYS = CUSTOMER_SYMBOL_GROUPS_SPACE_PARAMETER_SUPPORT.keys;

export const CUSTOMER_ALL_IN_TIERS_SPACE_PARAMETER_SUPPORT = defineDealerEntityAdminSpaceSupport({
  filters: {
    [DealerSpaceParameterKey.AllInTiers]: 'AllInTiers',
    [DealerSpaceParameterKey.Symbols]: 'Symbols',
    [DealerSpaceParameterKey.FeeModes]: 'FeeModes',
    [DealerSpaceParameterKey.FeeCurrencies]: 'FeeCurrencies',
    [DealerSpaceParameterKey.PricingAggregations]: 'PricingAggregations',
  },
  influence: {
    parameterKey: DealerSpaceParameterKey.Counterparty,
    customerConfigurationField: 'AllInTier',
  },
});
export const CUSTOMER_ALL_IN_TIERS_SPACE_PARAMETER_KEYS = CUSTOMER_ALL_IN_TIERS_SPACE_PARAMETER_SUPPORT.keys;

export const CUSTOMER_FEE_TIERS_SPACE_PARAMETER_SUPPORT = defineDealerEntityAdminSpaceSupport({
  filters: {
    [DealerSpaceParameterKey.FeeTiers]: 'FeeTiers',
    [DealerSpaceParameterKey.Symbols]: 'Symbols',
    [DealerSpaceParameterKey.FeeModes]: 'FeeModes',
    [DealerSpaceParameterKey.FeeCurrencies]: 'FeeCurrencies',
    [DealerSpaceParameterKey.PricingAggregations]: 'PricingAggregations',
  },
  influence: {
    parameterKey: DealerSpaceParameterKey.Counterparty,
    customerConfigurationField: 'FeeTier',
  },
});
export const CUSTOMER_FEE_TIERS_SPACE_PARAMETER_KEYS = CUSTOMER_FEE_TIERS_SPACE_PARAMETER_SUPPORT.keys;

export const CUSTOMER_PRICING_RULES_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparty',
  [DealerSpaceParameterKey.Symbols]: 'Symbols',
  [DealerSpaceParameterKey.Modes]: 'Modes',
  [DealerSpaceParameterKey.FeeModes]: 'FeeModes',
  [DealerSpaceParameterKey.FeeCurrencies]: 'FeeCurrencies',
  [DealerSpaceParameterKey.PricingAggregations]: 'PricingAggregations',
});
export const CUSTOMER_PRICING_RULES_SPACE_PARAMETER_KEYS = CUSTOMER_PRICING_RULES_SPACE_PARAMETER_SUPPORT.keys;

export const CUSTOMER_PRICING_RULES_MULTI_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
  [DealerSpaceParameterKey.Symbols]: 'Symbols',
  [DealerSpaceParameterKey.Modes]: 'Modes',
  [DealerSpaceParameterKey.FeeModes]: 'FeeModes',
  [DealerSpaceParameterKey.FeeCurrencies]: 'FeeCurrencies',
  [DealerSpaceParameterKey.PricingAggregations]: 'PricingAggregations',
});

export const CUSTOMER_SUB_ACCOUNT_MAPPINGS_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparty',
  [DealerSpaceParameterKey.Symbols]: 'Symbols',
});

export const CUSTOMER_SUB_ACCOUNT_MAPPINGS_MULTI_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
  [DealerSpaceParameterKey.Symbols]: 'Symbols',
});

export const CUSTOMER_AGGREGATIONS_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.PricingAggregations]: 'PricingAggregations',
  [DealerSpaceParameterKey.MarketAccounts]: 'MarketAccounts',
  [DealerSpaceParameterKey.Modes]: 'Modes',
});

export const CUSTOMER_ORDER_ACCEPTANCE_RULES_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
  [DealerSpaceParameterKey.Symbols]: 'Symbols',
});

export const CUSTOMER_RFQ_PROTOCOL_RULES_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
  [DealerSpaceParameterKey.Symbols]: 'Symbols',
});

export const CUSTOMER_ACCOUNTS_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
});

export const CUSTOMER_USERS_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
});

export const CUSTOMER_POSITIONS_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
});

export const CUSTOMER_FUNDING_RATE_CONFIGURATION_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Symbols]: 'Symbols',
});
